import Swal from "sweetalert2";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { AppState } from "src/app/store/index.selector";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { InstanceStatus } from "src/app/shared/types/instance-types";
import { TableService } from "src/app/shared/services/table.service";
import { ContractService } from "src/app/shared/services/contract.service";
import { InstanceService } from "src/app/shared/services/instance.service";
import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { BaseTableComponent, DatatableColumn } from "../../../shared/components/base-table/base-table.component";
import { ContractEnum, ContractType, RepositoryTagInfo } from "src/app/shared/types/contract-types";
import { UserRoles } from "src/app/shared/enums/user-roles";
import { MessagePortalComponent } from "src/app/portal/portal";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { toggleAnimation } from "src/app/shared/animations";

@Component({
  selector: "app-contract-table",
  templateUrl: "./contract-table.component.html",
  standalone: false,
  animations: [toggleAnimation],
})
export class ContractTableComponent extends BaseTableComponent<ContractType> implements OnInit {
  @ViewChild("addContractModalTemplate") addContractModalTemplate!: TemplateRef<unknown>;
  @ViewChild("deleteConfirmationTemplate") deleteConfirmationTemplate!: TemplateRef<unknown>;
  @ViewChild("datatable") datatable: any;
  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;

  addContractModal!: DialogRef;
  deleteModal!: DialogRef;
  private dialog = inject(DialogService);

  contractId = 0;

  selectedAgency: any;
  selectedCustomer: any;
  selectedConfig: any;
  isLoading = false;
  isLoadingDelete = false;
  searchText = "";
  userRole = UserRoles;
  contractToDeleteId = 0;
  deleteCustomer = true;

  constructor(
    public override translate: TranslateService,
    public override store: Store<AppState>,
    private contractService: ContractService,
    private instanceService: InstanceService,
    public override tableService: TableService,
    private positioningService: PositioningService,
  ) {
    super(store, tableService, `contracts/paginated`, translate);
  }

  isShowTaskMenu = false;
  dockerTags!: RepositoryTagInfo[];
  isSetConfigChecked = false;
  selectedFilter!: string;

  override ngOnInit(): void {
    super.ngOnInit();
  }

  getColumns(): void {
    this.cols = [
      { field: "id", title: "Id", sort: true, hide: true } as DatatableColumn,
      { field: "customerName", title: this.translate.instant("contract.customer_name"), sort: true } as DatatableColumn,
      { field: "agencyName", title: this.translate.instant("contract.agency_name"), sort: true } as DatatableColumn,
      { field: "domainName", title: this.translate.instant("contract.domain_name"), sort: true } as DatatableColumn,
      { field: "createdDate", title: this.translate.instant("contract.created_date"), sort: true } as DatatableColumn,
      { field: "validUntil", title: this.translate.instant("contract.valid_until"), sort: true, hide: true } as DatatableColumn,
      { field: "email", title: this.translate.instant("contract.email"), sort: true } as DatatableColumn,
      { field: "phone", title: this.translate.instant("contract.phone"), sort: true, hide: true } as DatatableColumn,
      { field: "contractTitle", title: this.translate.instant("contract.package"), sort: true, hide: true } as DatatableColumn,
      { field: "instanceStatus", title: this.translate.instant("contract.instance_status"), sort: false } as DatatableColumn,
      { field: "update", title: this.translate.instant("contract.update_software"), sort: false } as DatatableColumn,
      { field: "actions", title: this.translate.instant("contract.actions"), sort: false } as DatatableColumn,
    ];
  }

  onSetConfigChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.isSetConfigChecked = inputElement?.checked ?? false;
  }

  showLoadingModal(messageArg: string, popupType: PopupType) {
    setTimeout(() => {
      this.isLoading = false;
      const message = `${this.translate.instant(messageArg)}`;
      showMessage(popupType, message);
    }, 4500);
  }

  startInstance(contractId: number) {
    Swal.fire({
      icon: "warning",
      title: this.translate.instant("confirm_add_instance"),
      showCancelButton: true,
      confirmButtonText: this.translate.instant("Yes"),
      cancelButtonText: this.translate.instant("No"),
      customClass: {
        confirmButton: "swal2-confirm btn btn-primary",
        cancelButton: "swal2-cancel btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.isLoading = true;
        const addInstanceSub = this.instanceService.addInstance(contractId).subscribe({
          next: () => {
            addInstanceSub.unsubscribe();
            this.showLoadingModal("instance_added_valid", PopupType.Success);
            this.forceCheckUpdate();
          },
          error: () => {
            addInstanceSub.unsubscribe();
            this.showLoadingModal("instance_added_invalid", PopupType.Danger);
          },
        });
      }
    });
  }

  deleteInstance(contractId: number) {
    Swal.fire({
      icon: "warning",
      title: this.translate.instant("confirm_remove_instance"),
      showCancelButton: true,
      confirmButtonText: this.translate.instant("Yes"),
      cancelButtonText: this.translate.instant("No"),
      customClass: {
        confirmButton: "swal2-confirm btn btn-primary",
        cancelButton: "swal2-cancel btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.isLoading = true;
        const deleteSub = this.instanceService.deleteInstance(contractId).subscribe({
          next: () => {
            deleteSub.unsubscribe();
            this.showLoadingModal("instance_removed_valid", PopupType.Success);
            this.forceCheckUpdate();
          },
          error: () => {
            deleteSub.unsubscribe();
            this.showLoadingModal("instance_removed_invalid", PopupType.Danger);
          },
        });
      }
    });
  }

  addTask() {
    this.isShowTaskMenu = false;
    this.addContractModal = this.dialog.open(this.addContractModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  openDeleteModal(id: number) {
    this.contractToDeleteId = id;
    this.deleteModal = this.dialog.open(this.deleteConfirmationTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeDeleteModal() {
    this.deleteModal.close();
  }

  handleDelete() {
    if (this.contractToDeleteId > 0) {
      this.isLoadingDelete = true;
      const deleteSubscription = this.contractService.deleteContract(this.contractToDeleteId, this.deleteCustomer).subscribe({
        next: () => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          this.contractToDeleteId = 0;
          this.deleteModal.close();
          showMessage(PopupType.Success, this.translate.instant("contract.successfull_delete"));
          this.forceCheckUpdate();
        },
        error: () => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Danger, "Failed to delete contract");
        },
      });
    }
  }

  submittedNewContract() {
    this.addContractModal.close();
    this.forceCheckUpdate();
  }

  getInstanceStatusText(status: InstanceStatus): string {
    return InstanceStatus[status];
  }

  getContractTypeColor(contractStatus: ContractEnum): string {
    switch (contractStatus) {
      case ContractEnum.TrialRequested:
        return "bg-secondary";
      case ContractEnum.TrialExpired:
        return "bg-warning";
      case ContractEnum.PayedExpired:
        return "bg-success";
      default:
        return "";
    }
  }

  getContractTypeText(contractStatus: ContractEnum): string {
    return ContractEnum[contractStatus];
  }

  isTrialRequested(contractStatus: ContractEnum): boolean {
    return contractStatus === ContractEnum.TrialRequested;
  }

  isInstanceRunning(status: InstanceStatus): boolean {
    return status === InstanceStatus.Running;
  }

  forceCheckUpdate() {
    this.fetchItems(this.getPaginationParams(), true);
  }

  updateSoftware(contract: ContractType) {
    Swal.fire({
      icon: "warning",
      title: this.translate.instant("confirm_update_instance"),
      showCancelButton: true,
      confirmButtonText: this.translate.instant("Yes"),
      cancelButtonText: this.translate.instant("No"),
      customClass: {
        confirmButton: "swal2-confirm btn btn-primary",
        cancelButton: "swal2-cancel btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        const updateSub = this.contractService.updateSoftware(contract.id).subscribe({
          next: () => {
            updateSub.unsubscribe();
            const successMessage = this.translate.instant("update-software-succes");
            this.forceCheckUpdate();
            showMessage(PopupType.Success, successMessage);
          },
          error: () => {
            updateSub.unsubscribe();
            const errorMessage = this.translate.instant("update-software-failed");
            this.loading = false;
            showMessage(PopupType.Danger, errorMessage);
          },
        });
      }
    });
  }

  setSearchText(text: string) {
    setTimeout(() => {
      this.search = text;
    });
  }

  onPortalAttached() {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }
}
